import { Flex, Box, Text } from "rebass/styled-components";
import styled from "styled-components";
import Image from "next/image";
import Rating from "../common/Rating";
import Money from "../common/Money";
import Trans from "next-translate/Trans";
import Link from "../common/Link";
import Badge from "../common/Badge";
import { pick } from "lodash";
import { CurrencyIso, AvailablePromotion } from "../../interfaces";
import { MouseEventHandler, useContext } from "react";
import LiveAvatar from "../common/LiveAvatar";
import { Money as MoneyInterface, Review } from "../../generated/graphql";
import useTranslation from "next-translate/useTranslation";
import { LocaleContext } from "../locale";

interface Props {
  name: string;
  headline: string;
  avatar: string;
  price?: string;
  minPrice?: string;
  priceCurrency?: CurrencyIso;
  pricePer?: string;
  bookingEstimation?: MoneyInterface;
  address: string;
  repeatCustomersCount?: number;
  reviewsScore?: number;
  reviewsCount: number;
  completedBookingsCount: number;
  slug?: string;
  openInNewTab: boolean;
  queryParams?: any;
  hideHover?: boolean;
  holiday?: string;
  availablePromotions?: [AvailablePromotion];
  reviews?: any; // todo types
  city?: any; // todo types
  calendarUpToDate?: boolean;
  featuredReview: Review;
  searchDistanceAwayKm?: string;
  onMouseEnter?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
  shouldPrioritizeImage?: boolean; // set to true to boost image load performance when sitter card is above the fold
  slim?: boolean;
  availableServices: [{ serviceTypeId: string }];
  isSuperCaregiver?: boolean;
}

export const getContactParams = (query?: any) => {
  return pick(query, [
    "serviceTypeId",
    "serviceDates",
    "startDate",
    "endDate",
    "dogIds"
  ]);
};

const SitterCardContainer = styled(Box)`
  position: relative;
  cursor: pointer;
  border-bottom: 1px solid
    ${props => (!props.slim ? props.theme.colors.gray : "#fff")};
  padding-right: 8px;
  border-right: 8px solid #fff;
  &.nohover {
    padding-right: 0px;
  }
  &:not(.nohover):hover {
    border-right: ${props => `8px solid ${props.theme.colors.inuOrange}`};
  }
`;

const iconNameByServiceType = {
  "1": "boarding",
  "2": "dog-walking",
  "3": "house-sitting",
  "7": "day-care",
  "9": "drop-in-visits"
};

const SitterCard = (props: Props) => {
  const [id, title] = props.slug.split(/-(.+)/);
  const { openInNewTab, queryParams, hideHover } = props;
  const cleanParams = getContactParams(queryParams);
  const { t } = useTranslation();
  const { country } = useContext(LocaleContext);

  const renderTagsAndSnippetReview = () => {
    return (
      <>
        <Flex flexWrap="wrap">
          {props.isSuperCaregiver && (
            <Box mr={1} mb={"4px"}>
              <Badge primary icon="medal">
                {t("search:superCaregiver")}
              </Badge>
            </Box>
          )}

          {props.availableServices?.map(service => {
            // Disable visits service on Search Results
            if (service.serviceTypeId === "9") {
              return <></>;
            }

            return (
              <Box key={service.serviceTypeId} mr={1} mb={"4px"}>
                <Badge
                  outline
                  icon={`services/${
                    iconNameByServiceType[service.serviceTypeId]
                  }`}
                >
                  {t(`common:serviceType${service.serviceTypeId}SeoName`)}
                </Badge>
              </Box>
            );
          })}

          {props.availablePromotions?.map(promotion => (
            <Box key={promotion.id} mr={1} mb={"4px"}>
              <Badge primary>{promotion.tLabel}</Badge>
            </Box>
          ))}

          {!props.slim && !!(props.city || props.searchDistanceAwayKm) && (
            <Box mr={1} mb={"4px"}>
              <Badge icon="marker">
                {props.city?.name}{" "}
                {props.searchDistanceAwayKm &&
                  `- ${parseFloat(props.searchDistanceAwayKm).toFixed(2)} km`}
              </Badge>
            </Box>
          )}

          {!!props.repeatCustomersCount && (
            <Box mr={1} mb={"4px"}>
              <Badge icon="repeat">
                {props.repeatCustomersCount}{" "}
                <Trans i18nKey="common:repeatGuests" />
              </Badge>
            </Box>
          )}

          {!props.slim && props.calendarUpToDate && (
            <Box mr={1} mb={"4px"}>
              <Badge icon="calendar">
                <Trans i18nKey="common:calendarUpdated" />
              </Badge>
            </Box>
          )}

          {!props.slim && props?.holiday && (
            <Box mr={1} mb={"4px"}>
              <Badge>
                <Trans
                  i18nKey="common:availableOnHoliday"
                  values={{ holiday: props.holiday }}
                />
              </Badge>
            </Box>
          )}
        </Flex>

        {!props.slim && props.featuredReview && props.featuredReview.content && (
          <Flex flexDirection="row" ml={"-32px"}>
            <Box mr={1}>
              <LiveAvatar
                size={24}
                name={props.featuredReview.id}
                src={props.featuredReview.author.avatar?.thumb}
                alt={`${props.featuredReview.author.name}'s review`}
              />
            </Box>
            <Text
              flex={1}
              color="darkGray"
              sx={{
                overflow: "hidden",
                "text-overflow": "ellipsis",
                display: "-webkit-box",
                "-webkit-line-clamp": "2" /* number of lines to show */,
                " -webkit-box-orient": "vertical"
              }}
            >
              “{props.featuredReview.content.trim()}”
            </Text>
          </Flex>
        )}
      </>
    );
  };

  return (
    <Link
      href={{
        pathname: "/sitterprofile",
        query: { id: id, title: title },
        search: {
          ...cleanParams
        }
      }}
      target={openInNewTab ? `sitter_${id}` : "_self"}
      className={hideHover ? "nohover" : ""}
      css={{ textDecoration: "none", color: "inherit" }}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
    >
      <Box data-test="sitter-card">
        <SitterCardContainer
          flexWrap="nowrap"
          py={2}
          flex={1}
          height={"100%"}
          className={hideHover ? "nohover" : ""}
          {...props}
        >
          <Flex flexWrap="nowrap" flex={1}>
            <Box
              width="64px"
              height="64px"
              mr={2}
              sx={{
                position: "relative",
                ".image": {
                  borderRadius: "50%"
                }
              }}
            >
              <Image
                src={props.avatar}
                alt={`${props.name}: ${props.headline}`}
                className="image"
                priority={props.shouldPrioritizeImage}
                fill
                sizes="100vw"
              />
            </Box>

            <Flex flexDirection="column" flex={1}>
              <Flex>
                <Box flex={1}>
                  <Text
                    as="p"
                    sx={{
                      overflow: "hidden",
                      "text-overflow": "ellipsis",
                      display: "-webkit-box",
                      "-webkit-line-clamp": "2" /* number of lines to show */,
                      " -webkit-box-orient": "vertical",
                      margin: 0
                    }}
                  >
                    <Text
                      as="span"
                      variant="text.h2"
                      sx={{
                        color: "black",
                        fontSize: 3,
                        fontWeight: "book"
                      }}
                    >
                      {props.name}
                    </Text>
                    <Text as="span"> &#183; </Text>
                    <Text
                      as="span"
                      variant="text.h4"
                      sx={{
                        fontWeight: "normal",
                        textOverflow: "ellipsis"
                      }}
                    >
                      {props.headline}
                    </Text>
                  </Text>
                </Box>

                {(!!props.price || !!props.minPrice) && (
                  <Box ml={1}>
                    {!props.price && (
                      <Text
                        as="span"
                        sx={{
                          color: "inuOrange",
                          fontWeight: "book",
                          lineHeight: 1
                        }}
                      >
                        {t("common:fromPrice")}{" "}
                      </Text>
                    )}

                    <Text
                      as="span"
                      sx={{
                        color: "inuOrange",
                        fontWeight: "book",
                        lineHeight: 1
                      }}
                    >
                      <Money
                        currency={props.priceCurrency}
                        amount={props.price ?? props.minPrice}
                        stripZeros
                      />
                      <Text
                        as="span"
                        textAlign="right"
                        sx={{
                          textTransform: country != "de" && "lowercase"
                        }}
                      >
                        {!!props.price ? `/${props.pricePer}` : ""}
                      </Text>
                    </Text>

                    {props.bookingEstimation && (
                      <Box textAlign="right">
                        <Text as="span" color="darkGray" fontSize={0}>
                          <Money
                            currency={props.bookingEstimation?.currency}
                            amount={props.bookingEstimation?.amount}
                            stripZeros
                          />
                          <Text
                            as="span"
                            sx={{
                              textTransform: country != "de" && "lowercase"
                            }}
                          >
                            {" "}
                            {t("common:total")}
                          </Text>
                        </Text>
                      </Box>
                    )}
                  </Box>
                )}
              </Flex>

              {props.reviewsScore && (
                <Flex flexDirection="row" alignItems="center">
                  <Rating score={props.reviewsScore} />
                  <Text ml={1}>
                    <Text
                      as="span"
                      sx={{
                        color: "labradorBlack",
                        fontSize: 2,
                        lineHeight: 1
                      }}
                    >
                      {props.reviewsScore.toFixed(1)}
                    </Text>
                    <Text
                      as="span"
                      sx={{
                        color: "darkGray",
                        fontSize: 2,
                        lineHeight: 1
                      }}
                    >
                      {" "}
                      ({props.completedBookingsCount}{" "}
                      <Trans i18nKey="common:bookings" />)
                    </Text>
                  </Text>
                </Flex>
              )}

              <Box mt={1}>{renderTagsAndSnippetReview()}</Box>
            </Flex>
          </Flex>
        </SitterCardContainer>
      </Box>
    </Link>
  );
};

SitterCard.defaultProps = {
  openInNewTab: false,
  queryParams: null,
  shouldPrioritizeImage: false
};
export default SitterCard;
